import { LabelValuePair } from "../../../global/formFields/staticSelect/LabelValuePair";
import { TimetrackingRequestDto } from "./workSchedule";
import { AbsentStatus } from "./employee";

export enum WorkContractStatus {
  ALL = "ALL",

  ACTIVE = "ACTIVE",

  /** Former employees whose contracts have ended */
  FORMER = "FORMER",

  /** Employees whose contracts will end with-in the next 14 days */
  EXPIRING_14 = "EXPIRING_14",

  /** Employees whose contracts will end with-in the next 30 days */
  EXPIRING_30 = "EXPIRING_30",

  /** Employees whose contracts recently started in the past 14 days  */
  RECENTLY_ACTIVE_14 = "RECENTLY_ACTIVE_14",

  /** Employees whose contracts recently started in the past 30 days  */
  RECENTLY_ACTIVE_30 = "RECENTLY_ACTIVE_30",

  /** Employees whose contracts have not started yet */
  FUTURE = "FUTURE",

  /** Employees whose contracts will start in the next 14 days */
  FUTURE_14 = "FUTURE_14",

  /** Employees whose contracts started in the past week or will start in the next week  */
  BACK_AND_FORTH_7 = "BACK_AND_FORTH_7",

  /** Employees whose contracts will start in 14 days and all active employees */
  BACK_14_AND_ACTIVE = "BACK_14_AND_ACTIVE",
}

export interface WorkContractUpdate {
  uuid: string;
  startDate: string;
  endDate: string | null;
  position: string;
  contractType: string;
  arbeitnehmerTyp: ArbeitnehmerTyp;
  workingHoursPerWeek: number;
  effectiveDate?: string;
  companyId: string;
  departmentId: string;
  locationId: string;
  comment?: string;
  endDateCheck?: boolean;
  vertragsForm: Vertragsform;
  additionalContractInformation: string;
  offboardingTemplateUuid: string;
}

export enum WorkingStatus {
  NOT_WORKING = "NOT_WORKING",
  WORKING = "WORKING",
  ON_BREAK = "ON_BREAK",
}

export enum TimeTrackingFilter {
  WORKING = "WORKING",
  ON_BREAK = "ON_BREAK",
  OVERTIME = "OVERTIME",
}

export interface WorkContractDto {
  accountId?: string;
  employeeId: string;
  uuid: string;
  startDate: string;
  endDate: string;
  position: string;
  contractType: string;
  arbeitnehmerTyp: ArbeitnehmerTyp;
  workingHoursPerWeek: number;
  professionalEmail?: string;
  company: LabelValuePair<string>;
  department: LabelValuePair;
  location: LabelValuePair;
  missingEmployeeData: boolean;
  managedCompanies: LabelValuePair[];
  managedDepartments: LabelValuePair[];
  workContractChanges: ChangesResponse[];
  vertragsForm: Vertragsform;
  working?: WorkingStatus;
  absent: AbsentStatus;
  workingTimetrackingRequest?: TimetrackingRequestDto;
  mainEmployment: boolean | null;
  additionalContractInformation: string;
}

export interface JobDto {
  uuid: string;
  sicknessCertificateDays: number;
  waitingPeriod: boolean;
  abrechnungsGruppe: AbrechnungsGruppe;
  probationPeriod: string;
  holidayBudget: number;
  probationTerminationPeriod: string;
  probationTerminationEffectiveness: string;
  terminationPeriod: string;
  terminationEffectiveness: string;
  changes: ChangesResponse[];
  personalNr: string;
  effectiveDate?: string;
  comment?: string;
  costUnit?: { label: string; value: string };
}

export interface StatisticsRequest {
  uuid?: string;
  taetigkeitId: number;
  schulabschluss: number;
  ausbildungsabschluss: number;
  arbeitnehmerUeberlassung: boolean;
  effectiveDate?: string;
  comment?: string;
}
export enum Schulabschluss {
  OHNE_ABSCHLUSS = 1,
  HAUPT_VOLKSSCHULABSCHLUSS = 2,
  MITTLERE_REIFE = 3,
  ABITUR_FACHABITUR = 4,
  UNBEKANNT = 9,
}

export const menuItemsSchulabschluss = [
  {
    label: "educationLevel.noDegree",
    value: Schulabschluss.OHNE_ABSCHLUSS,
  },
  {
    label: "educationLevel.primarySchool",
    value: Schulabschluss.HAUPT_VOLKSSCHULABSCHLUSS,
  },
  {
    label: "educationLevel.intermediateSchool",
    value: Schulabschluss.MITTLERE_REIFE,
  },
  {
    label: "educationLevel.universityCollege",
    value: Schulabschluss.ABITUR_FACHABITUR,
  },
  { label: "educationLevel.unknown", value: Schulabschluss.UNBEKANNT },
];

export enum Ausbildungsabschluss {
  OHNE_BERUFLICHEN_ABSCHLUSS = 1,
  ABSCHLUSS_ANERKANNTE_BERUFSAUSBILDUNG = 2,
  MEISTER_TECHNIKER = 3,
  BACHELOR = 4,
  DIPLOM_MAGISTER_STAATSEXAMEN = 5,
  PROMOTION = 6,
  UNBEKANNT = 9,
}

export const ausbildungsAbschlussmenuItems = [
  {
    label: "OHNE_BERUFLICHEN_ABSCHLUSS",
    value: Ausbildungsabschluss.OHNE_BERUFLICHEN_ABSCHLUSS,
  },
  {
    label: "ABSCHLUSS_ANERKANNTE_BERUFSAUSBILDUNG",
    value: Ausbildungsabschluss.ABSCHLUSS_ANERKANNTE_BERUFSAUSBILDUNG,
  },
  {
    label: "MEISTER_TECHNIKER",
    value: Ausbildungsabschluss.MEISTER_TECHNIKER,
  },
  { label: "BACHELOR", value: Ausbildungsabschluss.BACHELOR },
  {
    label: "DIPLOM_MAGISTER_STAATSEXAMEN",
    value: Ausbildungsabschluss.DIPLOM_MAGISTER_STAATSEXAMEN,
  },
  { label: "PROMOTION", value: Ausbildungsabschluss.PROMOTION },
  { label: "UNBEKANNT", value: Ausbildungsabschluss.UNBEKANNT },
];

export enum Vertragsform {
  VOLLZEIT_UNBEFRISTET = 1,
  VOLLZEIT_BEFRISTET = 3,
  TEILZEIT_BEFRISTET = 4,
  TEILZEIT_UNBEFRISTET = 2,
}

export const vertragsFormMenuItems = [
  {
    label: "VOLLZEIT_BEFRISTET",
    value: Vertragsform.VOLLZEIT_BEFRISTET,
  },
  {
    label: "VOLLZEIT_UNBEFRISTET",
    value: Vertragsform.VOLLZEIT_UNBEFRISTET,
  },
  {
    label: "TEILZEIT_BEFRISTET",
    value: Vertragsform.TEILZEIT_BEFRISTET,
  },
  {
    label: "TEILZEIT_UNBEFRISTET",
    value: Vertragsform.TEILZEIT_UNBEFRISTET,
  },
];

export interface StatisticsView {
  uuid: string;
  taetigkeitId: number;
  ausgeuebteTaetigkeit: string;
  schulabschluss: Schulabschluss;
  ausbildungsabschluss: Ausbildungsabschluss;
  arbeitnehmerUeberlassung: boolean;
  taetigkeitsSchluessel: string;
  changes: ChangesResponse[];
}

export enum AbrechnungsGruppe {
  ERSTABRECHNUNG = "ERSTABRECHNUNG",
  FOLGEGRUPPE = "FOLGEGRUPPE",
}

export enum ArbeitnehmerTyp {
  ANGESTELLTER = "ANGESTELLTER",
  ARBEITER = "ARBEITER",
  GEWERBLICHER_AUSZUBILDENDER = "GEWERBLICHER_AUSZUBILDENDER",
  KAUFMAENNISCHER_AUSZUBILDENDER = "KAUFMAENNISCHER_AUSZUBILDENDER",
  GERINGVERDIENENDER_PRAKTIKANT = "GERINGVERDIENENDER_PRAKTIKANT",
  GERINGFUEGIG_BESCHAEFTIGTER_ARBEITER_GGF_PAUSCHALIERUNG = "GERINGFUEGIG_BESCHAEFTIGTER_ARBEITER_GGF_PAUSCHALIERUNG",
  GERINGFUEGIG_BESCHAEFTIGTER_ANGESTELLTER_GGF_PAUSCHALIERUNG = "GERINGFUEGIG_BESCHAEFTIGTER_ANGESTELLTER_GGF_PAUSCHALIERUNG",
  KURZFRISTIG_BESCHAEFTIGT_ARBEITER_PAUSCHAL = "KURZFRISTIG_BESCHAEFTIGT_ARBEITER_PAUSCHAL",
  KURZFRISTIG_BESCHAEFTIGT_ANGESTELLTER_PAUSCHAL = "KURZFRISTIG_BESCHAEFTIGT_ANGESTELLTER_PAUSCHAL",
  WERKSTUDENT = "WERKSTUDENT",
  FREELANCER = "FEELANCER",
}

export interface ChangesResponse {
  uuid: string;
  creationDate: string;
  updateDate: string;
  principal: string;
  principalUserId: string;
  effectiveDate: string;
  changeStatus: ChangeApprovalStatus;
  executed: boolean;
  comment: string;
  changes: ChangeDto[];
  changeResource: ChangeResource;
}

export interface ChangeDto {
  uuid: string;
  changeType: ChangeType;
  newValue: string;
  newLabel: string;
  oldValue: string;
  oldLabel: string;
}

export enum ChangeApprovalStatus {
  REQUESTED = "REQUESTED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  NOT_REQUIRED = "NOT_REQUIRED",
}

export enum ChangeResource {
  PERSONAL = "PERSONAL",
  TAX_SOCIAL = "TAX_SOCIAL",
  GENERAL = "GENERAL",
  JOB = "JOB",
  STATISTICS = "STATISTICS",
  BUDGET = "BUDGET",
  COMPENSATION = "COMPENSATION",
  BANK = "BANK",
}

export enum ChangeType {
  personalNr = "personalNr",
  contractType = "contractType",
  nationality = "nationality",
  waitingPeriod = "waitingPeriod",
  startDate = "startDate",
  endDate = "endDate",
  position = "position",
  arbeitnehmerTyp = "arbeitnehmerTyp",
  department = "department",
  location = "location",
  sicknessCertificateDays = "sicknessCertificateDays",
  abrechnungsGruppe = "abrechnungsGruppe",
  probationPeriod = "probationPeriod",
  probationTerminationPeriod = "probationTerminationPeriod",
  probationTerminationEffectiveness = "probationTerminationEffectiveness",
  terminationPeriod = "terminationPeriod",
  terminationEffectiveness = "terminationEffectiveness",
  ausgeuebteTaetigkeit = "ausgeuebteTaetigkeit",
  schulabschluss = "schulabschluss",
  ausbildungsabschluss = "ausbildungsabschluss",
  arbeitnehmerUeberlassung = "arbeitnehmerUeberlassung",
  vertragsForm = "vertragsForm",
  personenGruppenSchluessel = "personenGruppenSchluessel",
  taetigkeitsSchluessel = "taetigkeitsSchluessel",
  budgetInc = "budgetInc",
  budgetDec = "budgetDec",
  compensationInc = "compensationInc",
  compensationDec = "compensationDec",
  firstName = "firstName",
  lastName = "lastName",
  street = "street",
  postalCode = "postalCode",
  city = "city",
  email = "email",
  phoneNumber = "phoneNumber",
  sex = "sex",
  taxId = "taxId",
  taxClass = "taxClass",
  birthDate = "birthDate",
  svNumber = "svNumber",
  privateInsured = "privateInsured",
  beitragPkv = "beitragPkv",
  beitragPpv = "beitragPpv",
  betriebsNummerKrankenkasse = "betriebsNummerKrankenkasse",
  employerContribution = "employerContribution",
  privatePension = "privatePension",
  mainEmployment = "mainEmployment",
  iban = "iban",
  bic = "bic",
  childBenefit = "childBenefit",
  degreeOfDisability = "degreeOfDisability",
  churchMembership = "churchMembership",
  academicTitle = "academicTitle",
  paymentMethod = "paymentMethod",
  svNumberAvailable = "svNumberAvailable",
  taxIdAvailable = "taxIdAvailable",
}
